<template>
    <div class="app-start">

        <Transition name="fade" mode="out-in" appear>
            <div class="banner" :key="banner">

                <!-- banner: no subscription -->
                <BannerSubscription v-if="isBannerSubscription" />

                <!-- banner: trial subscription -->
                <BannerTrial v-else-if="isBannerTrial" />

                <!-- banner: survey -->
                <BannerSurvey v-else-if="isBannerSurvey">
                    <v-btn 
                        elevation="0" 
                        @click="handleSurvey">
                        Open Survey (3 min)
                    </v-btn>
                </BannerSurvey>

                <!-- banner: welcome -->
                <BannerWelcome v-else />

            </div>
        </Transition>

        <!-- subscription -->
        <div class="section">
            <!--<HeadingBase class="heading">Your Subscription</HeadingBase>-->
            <Subscription />
        </div>

        <!-- apps -->
        <div class="section">
            <!--<HeadingBase class="heading">Getting Started</HeadingBase>-->
            <Guidance v-if="!hasApps" text="No apps available." />
            <div v-else class="grid">
                <ItemApp 
                    v-for="app in apps.filter(app => app.enabled)" 
                        :route="app.route" 
                        :image="app.image" 
                        :name="app.name"
                        :description="app.description" />
            </div>
        </div>

        <!-- models -->
        <div class="section">
            <HeadingBase class="heading">Recent Models</HeadingBase>
            <CarouselList :assets="models"></CarouselList>
        </div>

        <!-- updates -->
        <div class="section">
            <HeadingBase class="heading">Latest Updates</HeadingBase>
            <NewsTable>
                <NewsEntry 
                    v-for="item in news" 
                    :image="item.image" 
                    :route="item.route">
                    <template #date>{{ item.date }}</template>
                    <template #title>{{ item.title }}</template>
                    <template #text>
                        <span v-html="item.text"></span>
                    </template>
                </NewsEntry>
            </NewsTable>
        </div>

        <div class="spacer"></div>

    </div>
</template>

<script>
import ROUTES from '@generio-ai/library-legacy/constants/routes'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { HeadingBase } from '@generio-ai/library-legacy/components/headings'
import Alert from '@/components/common/alert/Alert.vue'
import BannerSubscription from '@/components/common/banner/BannerSubscription.vue'
import BannerTrial from '@/components/common/banner/BannerTrial.vue'
import BannerSurvey from '@/components/common/banner/BannerSurvey.vue'
import BannerWelcome from '@/components/common/banner/BannerWelcome.vue'
import Guidance from '@/components/common/guidance/Guidance.vue'
import CarouselList from '@/components/start/carousel/CarouselList.vue'
import ItemApp from '@/components/start/items/ItemApp.vue'
import NewsTable from '@/components/start/news/NewsTable.vue'
import NewsEntry from '@/components/start/news/NewsEntry.vue'
import Subscription from '@/components/start/subscription/Subscription.vue'

export default {
    name: 'AppStart',
    components: {
        HeadingBase,
        Alert,
        BannerSubscription,
        BannerTrial,
        BannerSurvey,
        BannerWelcome,
        Guidance,
        CarouselList,
        ItemApp,
        NewsTable,
        NewsEntry,
        Subscription
    },
    data() {
        return {
            loading: false,
            surveys: null
        }
    },
    computed: {
        ...mapGetters('auth', [
            'hasApps',
            'hasAppCreate',
            'hasAppModify',
            'hasAppLibrary',
            'hasAppMarket',
            'hasAppTutorials',
            'hasAppCommunity'
        ]),
        ...mapGetters('library', [
            'getFilteredModelAssets'
        ]),
        ...mapGetters('subscriptions', [
            'isLoading',
            'isSubscriptionNone',
            'isSubscriptionTrial'
        ]),

        keyBannerSubscription() {
            return 'subscription'
        },
        keyBannerTrial() {
            return 'trial'
        },
        keyBannerSurvey() {
            return 'survey'
        },
        keyBannerWelcome() {
            return 'welcome'
        },

        hasSurveys() {
            if (!Array.isArray(this.surveys)) return false
            return this.surveys.length > 0
        },

        isBannerSubscription() {
            return this.banner === this.keyBannerSubscription
        },
        isBannerTrial() {
            return this.banner === this.keyBannerTrial
        },
        isBannerSurvey() {
            return this.banner === this.keyBannerSurvey
        },
        isBannerWelcome() {
            return this.banner === this.keyBannerWelcome
        },

        banner() {
            if (this.isLoading) return null
            if (this.isSubscriptionNone) return this.keyBannerSubscription
            if (this.isSubscriptionTrial) return this.keyBannerTrial
            if (this.hasSurveys) return this.keyBannerSurvey
            return this.keyBannerWelcome
        },

        news() {
            return [
                {
                    date: '2026-10',
                    title: 'Our new model texture generation available now',
                    image: '/images/news/202610_texture.png',
                    text: 'We have launched our new projection-based texture generation, which greatly improves the quality of text, logos, and faces on 3D models.<br /><br />' +
                         'The classic texture generation remains available as "native", alongside the new options "project" and "hybrid". ' +
                         'The latter combines projection-based texturing with native generation, reaching best-in-class results.',
                    route: ROUTES.APPSUITE.CREATE
                },
                {
                    date: '2026-07',
                    title: 'Print-ready models with our new watertight processing',
                    image: '/images/news/202607_watertight.png',
                    text: 'Our new watertight processing ensures that 3D models are print-ready by automatically fixing geometry issues and closing gaps.' +
                         'The option automatically appears as "make watertight" for models that require it.',
                    route: ROUTES.APPSUITE.MODIFY
                },
                {
                    date: '2026-02',
                    title: 'Next version of our model geometry generation goes live',
                    image: '/images/news/202602_ultra.png',
                    text: 'We are thrilled to announce our next version of <i>our model generation: v3.0!</i> <i>Generate stunning 3D models</i> with great geometric detail.<br /><br />' +
                        'For the first time, we offer three levels of details: low, high, and ultra. Moreover, we introduce a toggle to switch ' +
                        'between "solid" and "surface" 3D models. With "solid" models are automatically watertight, whereas "surface" allows open surfaces.',
                    route: ROUTES.APPSUITE.CREATE
                },
                {
                    date: '2025-12',
                    title: 'Get started with our API, available in all paid subscriptions',
                    image: '/images/news/202512_api.png',
                    text: 'We’re excited to announce that GENERIO’s API is now available to all subscribers, marking a major step forward in fully automated 3D model production.<br /><br />' +
                        'Whether you want to automatically generate production-ready 3D models or optimize existing assets, our API gives you the flexibility to do it with ease.',
                    route: ROUTES.DOCUMENTATION.ROOT
                },
                {
                    date: '2025-11',
                    title: 'Reduce the polygon count down to 1k with model optimization',
                    image: '/images/news/202511_optimize.png',
                    text: 'Users can now drastically lower the polygon count of their generated 3D models, down to as little as 1,000 polygons.<br /><br />' +
                        'This improvement makes models significantly more lightweight and far easier to deploy across performance-sensitive environments such as web applications, mobile platforms, and immersive experiences.',
                    route: ROUTES.APPSUITE.LIBRARY_MODELS
                }
            ]
        },

        apps() {
            return [
                {
                    name: 'Create Model',
                    description: 'Create 3D models from sketches, images, or text.',
                    image: '/images/apps/create.png',
                    route: ROUTES.APPSUITE.CREATE,
                    enabled: this.hasAppCreate
                },
                {
                    name: 'Modify Model',
                    description: 'Modify your <i>3D models</i> texture or geometry.',
                    image: '/images/apps/modify.png',
                    route: ROUTES.APPSUITE.MODIFY,
                    enabled: this.hasAppModify
                },
                /*
                {
                    name: 'Library',
                    description: 'Browse through all your <i>3D models</i> and images.',
                    image: '/images/apps/library.png',
                    route: ROUTES.APPSUITE.LIBRARY,
                    enabled: this.hasAppLibrary
                },
                {
                    name: 'Market',
                    description: 'Explore and download <i>3D models</i> from our market.',
                    image: '/images/apps/market.png',
                    route: ROUTES.APPSUITE.MARKET,
                    enabled: this.hasAppMarket
                },
                {
                    name: 'Tutorials',
                    description: 'Learn how to make the most out of Generio.',
                    image: '/images/apps/lab.png',
                    route: ROUTES.APPSUITE.TUTORIALS,
                    enabled: this.hasAppTutorials
                },
                {
                    name: 'Community',
                    description: 'Join the Generio community and share your creations.',
                    image: '/images/apps/community.png',
                    route: ROUTES.APPSUITE.COMMUNITY,
                    enabled: this.hasAppCommunity
                }
                */
            ]
        },

        models() {
            return this.getFilteredModelAssets
        }
    },
    methods: {
        ...mapActions('api', [
            'analyticsSurveysGet'
        ]),
        ...mapActions('dialogs', [
            'open'
        ]),

        async retrieveSurveys() {
            this.loading = true
            try {
                const responseSurveys = await this.analyticsSurveysGet()
                this.surveys = responseSurveys?.surveys?.available
            }
            finally {
                this.loading = false
            }
        },
        handleSurvey() {
            if (!this.hasSurveys) return

            // directly open survey if there is one
            if (this.surveys.length === 1) {
                const id = this.surveys[0]
                this.$router.push(`/survey?id=${id}`)
            }
            // open survey overview if there are multiple
            else {
                this.$router.push(`/survey`)
            }
        },
        handleSelect(asset) {
            this.open({
                component: 'open',
                payload: {
                    asset
                }
            })
        }
    },
    mounted() {
        this.retrieveSurveys()
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}

.app-start {
    width: 100%;
    height: 100%;
    min-height: 100vh;
    display: block;
    overflow-y: auto;
    background: rgb(var(--v-theme-darkbase));
}

.app-start .banner {
    width: 100%;
}

.app-start .section {
    display: block;
    width: 100%;
    max-width: 1200px;
    padding: 25px;
    margin: 0px auto;
}

.app-start .section .heading {
    margin-bottom: 20px;
}

.app-start .section .grid {
    width: 100%;
    margin-top: 25px;
    margin-bottom: 25px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    justify-content: flex-start;
    flex-shrink: 1;
}

.app-start .spacer {
    height: 50px;
}

@media (max-width: 1600px) {
    .app-start .section .grid {
        margin-top: 20px;
        margin-bottom: 20px;
    }
}

@media (max-width: 1200px) {
    .app-start .section .grid {
        margin-top: 15px;
        margin-bottom: 15px;
    }
}

@media (max-width: 600px) {
    .app-start .section {
        padding: 15px;
    }
}

@media (max-width: 400px) {
    .grid {
        grid-template-columns: repeat(1, 1fr);
    }
}
</style>